import { IBox } from './exportLabelTypes'

// Clear space kept between two icons once they have been moved apart.
const MARKER_GAP_PX = 3
// How far, in icon sizes, an icon may stray from its true point before the leader stops being
// believable; past it the icon stays where the pushing left it.
const MAX_SHIFT_ICONS = 2.5
const MAX_PASSES = 60
// Coincident icons have no direction to part along, so each takes its own turn of the golden angle.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

export interface IMovableMarker {
  key: string
  icon: IBox
}

interface IDisk {
  key: string
  x: number
  y: number
  r: number
  originX: number
  originY: number
  maxShift: number
}

const diskOf = (key: string, box: IBox, maxShift = 0): IDisk => {
  const x = box.x + box.width / 2
  const y = box.y + box.height / 2
  return {
    key,
    x,
    y,
    // The half-diagonal: square icons that clear these circles cannot touch even corner to corner.
    r: Math.hypot(box.width, box.height) / 2,
    originX: x,
    originY: y,
    maxShift,
  }
}

const clampToOrigin = (disk: IDisk): void => {
  const dx = disk.x - disk.originX
  const dy = disk.y - disk.originY
  const distance = Math.hypot(dx, dy)
  if (distance > disk.maxShift) {
    disk.x = disk.originX + (dx / distance) * disk.maxShift
    disk.y = disk.originY + (dy / distance) * disk.maxShift
  }
}

// How far apart two disks must still go, and the unit direction from a to b.
const separation = (
  a: IDisk,
  b: IDisk,
  turn: number,
): { need: number; ux: number; uy: number } => {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const distance = Math.hypot(dx, dy)
  const need = a.r + b.r + MARKER_GAP_PX - distance
  if (distance > 1e-6) {
    return { need, ux: dx / distance, uy: dy / distance }
  }
  const angle = turn * GOLDEN_ANGLE
  return { need, ux: Math.cos(angle), uy: Math.sin(angle) }
}

// Apart by more than they need on one axis alone: the pair needs nothing, so it is skipped before
// the distance is worked out.
const isClear = (a: IDisk, b: IDisk): boolean => {
  const reach = a.r + b.r + MARKER_GAP_PX + 1
  return Math.abs(b.x - a.x) >= reach || Math.abs(b.y - a.y) >= reach
}

// Overlapping Location icons are pushed apart, each the least it can, and away from anything that
// does not move (cluster markers, pins). Deterministic: the same scene always settles the same way.
function settleMarkers(
  movable: IMovableMarker[],
  fixed: IBox[],
): Map<string, { x: number; y: number }> {
  const disks = [...movable]
    .sort((a, b) => a.key.localeCompare(b.key))
    .map(m =>
      diskOf(
        m.key,
        m.icon,
        MAX_SHIFT_ICONS * Math.max(m.icon.width, m.icon.height),
      ),
    )
  const walls = fixed.map((box, index) => diskOf(`fixed:${index}`, box))

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    let isMoving = false
    disks.forEach((a, i) => {
      for (let j = i + 1; j < disks.length; j++) {
        const b = disks[j]
        if (isClear(a, b)) continue
        const { need, ux, uy } = separation(a, b, i + j)
        if (need <= 0) continue
        a.x -= (ux * need) / 2
        a.y -= (uy * need) / 2
        b.x += (ux * need) / 2
        b.y += (uy * need) / 2
        isMoving = true
      }
      walls.forEach((wall, w) => {
        if (isClear(wall, a)) return
        const { need, ux, uy } = separation(wall, a, i + w)
        if (need <= 0) return
        a.x += ux * need
        a.y += uy * need
        isMoving = true
      })
      clampToOrigin(a)
    })
    if (!isMoving) break
  }

  const moved = new Map<string, { x: number; y: number }>()
  disks.forEach(disk => {
    if (Math.hypot(disk.x - disk.originX, disk.y - disk.originY) > 0.5) {
      moved.set(disk.key, { x: disk.x, y: disk.y })
    }
  })
  return moved
}

// Every number the settling reads, in order: marker keys set the order icons settle in.
const sceneOf = (
  movable: IMovableMarker[],
  fixed: IBox[],
): { keys: string[]; numbers: number[] } => ({
  keys: movable.map(m => m.key),
  numbers: [...movable.map(m => m.icon), ...fixed].flatMap(box => [
    box.x,
    box.y,
    box.width,
    box.height,
  ]),
})

const isSameList = <T>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every((value, i) => value === b[i])

let last: {
  scene: ReturnType<typeof sceneOf>
  moved: Map<string, { x: number; y: number }>
} | null = null

// A re-solve that moves no icon (name sizes, label tones) gets the last answer back without settling
// again.
export function displaceMarkers(
  movable: IMovableMarker[],
  fixed: IBox[],
): Map<string, { x: number; y: number }> {
  const scene = sceneOf(movable, fixed)
  if (
    !last ||
    !isSameList(last.scene.keys, scene.keys) ||
    !isSameList(last.scene.numbers, scene.numbers)
  ) {
    last = { scene, moved: settleMarkers(movable, fixed) }
  }
  return new Map(last.moved)
}
